import React, { useRef, useState } from "react";
import "./personalnote.css";
import { notes } from "../../data/site";
import { projects } from "../work/Data";
import NoteArt from "../visuals/NoteArt";
import Reveal from "../ui/Reveal";
import Icon from "../ui/Icon";

const titleOf = Object.fromEntries(projects.map((p) => [p.slug, p.title]));

/**
 * Lab notebook: a numbered index on the left, one plate on the right. The index
 * auto-advances (a progress line fills under the active entry), pauses while the
 * pointer or focus is inside, and follows the WAI-ARIA vertical tabs pattern.
 * Under reduced motion the progress animation is off, so nothing auto-advances.
 */
const Personalnote = ({ onOpenProject }) => {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const tabRefs = useRef([]);
  const note = notes[active];

  const go = (i, focus = false) => {
    const next = (i + notes.length) % notes.length;
    setActive(next);
    if (focus) tabRefs.current[next]?.focus();
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown" || e.key === "ArrowRight") {
      e.preventDefault();
      go(active + 1, true);
    } else if (e.key === "ArrowUp" || e.key === "ArrowLeft") {
      e.preventDefault();
      go(active - 1, true);
    } else if (e.key === "Home") {
      e.preventDefault();
      go(0, true);
    } else if (e.key === "End") {
      e.preventDefault();
      go(notes.length - 1, true);
    }
  };

  return (
    <section className="notes section" id="notes" aria-labelledby="notes-title">
      <div className="container">
        <Reveal as="header" className="section__head">
          <span className="eyebrow">03 — Engineering notes</span>
          <h2 id="notes-title" className="section__title">
            Lessons from building my own systems
          </h2>
          <p className="section__lead">What I found, what I did, and what I check next time.</p>
        </Reveal>

        <Reveal
          className="notebook"
          data-paused={paused || undefined}
          onPointerEnter={() => setPaused(true)}
          onPointerLeave={() => setPaused(false)}
          onFocus={() => setPaused(true)}
          onBlur={() => setPaused(false)}
        >
          <div className="notebook__index" role="tablist" aria-orientation="vertical" aria-label="Engineering notes" onKeyDown={onKeyDown}>
            {notes.map((n, i) => (
              <button
                key={n.title}
                ref={(el) => {
                  tabRefs.current[i] = el;
                }}
                type="button"
                role="tab"
                id={`note-tab-${i}`}
                aria-selected={i === active}
                aria-controls="note-panel"
                tabIndex={i === active ? 0 : -1}
                className="notebook__tab"
                onClick={() => go(i)}
              >
                <span className="notebook__num">{String(i + 1).padStart(2, "0")}</span>
                <span className="notebook__tab-title">{n.title}</span>
                <span className="notebook__tab-lesson">{n.lesson}</span>
                {i === active && <span className="notebook__progress" onAnimationEnd={() => go(active + 1)} aria-hidden="true" />}
              </button>
            ))}
          </div>

          <article key={active} className="notebook__plate" id="note-panel" role="tabpanel" aria-labelledby={`note-tab-${active}`} tabIndex={0}>
            <div className="notebook__bar" aria-hidden="true">
              <span>NOTE {String(active + 1).padStart(2, "0")} / {String(notes.length).padStart(2, "0")}</span>
              <span>{note.project}</span>
            </div>
            <div className="notebook__body">
              <div className="notebook__art">
                <NoteArt visual={note.visual} />
              </div>
              <div className="notebook__text">
                <h3 className="notebook__title">{note.title}</h3>
                <p className="notebook__copy">{note.body}</p>
                <p className="notebook__lesson">
                  <span>Lesson</span> {note.lesson}
                </p>
                <button type="button" className="notebook__link" onClick={() => onOpenProject(note.project)}>
                  From the {titleOf[note.project].replace(/^The /, "")} case file <Icon name="arrow" />
                </button>
              </div>
            </div>
          </article>
        </Reveal>
      </div>
    </section>
  );
};

export default Personalnote;
